import React, { useState } from "react";
import { toast } from "sonner";
import { Search, MapPin, Globe, Phone, Clock, Loader2, ExternalLink } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { currentCategories } from "@/lib/categories";
import { COUNTRIES } from "@/lib/places";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const fld = "w-full h-11 rounded-xl border border-slate-200 px-3 text-sm bg-white";
const cats = () => currentCategories().filter((c) => !["komunitete", "punekerkues"].includes(c.key));
const hoursText = (w) => (w ? w.filter((h) => h.open).map((h) => `${h.day} ${h.h24 ? "24 orë" : `${h.from}-${h.to}`}`).join(", ") : "");

function Results({ items, onPick }) {
  return (
    <div className="space-y-2" data-testid="biz-results">
      {items.map((r) => (
        <button key={r.osm} data-testid={`biz-result-${r.osm}`} onClick={() => onPick(r)} className="w-full text-left bg-white rounded-2xl border border-slate-100 p-3 hover:border-emerald-300">
          <div className="font-semibold text-slate-800 text-sm" translate="no">{r.name}</div>
          <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" />{[r.address, r.city, r.country].filter(Boolean).join(", ")}</div>
          {r.kind && <div className="text-[11px] text-slate-400 mt-0.5">{r.kind}</div>}
        </button>
      ))}
    </div>
  );
}

function Preview({ f }) {
  const row = (Icon, v) => v && <div className="flex items-start gap-2 text-sm text-slate-600"><Icon className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" /><span className="break-all">{v}</span></div>;
  return (
    <div className="space-y-2" data-testid="biz-preview">
      <div className="flex items-center gap-3">
        <div className="w-16 h-16 rounded-2xl bg-slate-100 overflow-hidden flex items-center justify-center font-bold text-slate-400 text-xl shrink-0">
          {f.logo ? <img data-testid="biz-logo" src={f.logo} alt="" className="w-full h-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : f.title?.[0]}
        </div>
        <div className="min-w-0"><div className="font-bold text-slate-900" translate="no">{f.title}</div><div className="text-xs text-slate-500">{[f.city, f.country].filter(Boolean).join(", ")}</div></div>
      </div>
      {row(MapPin, f.address)}
      {row(Phone, f.phone)}
      {row(Globe, f.website)}
      {row(Clock, hoursText(f.hours_week) || f.hours_raw)}
      {f.lat != null && <a data-testid="biz-gmaps" href={`https://www.google.com/maps/search/?api=1&query=${f.lat},${f.lng}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-sm text-emerald-700 font-semibold"><ExternalLink className="w-4 h-4" />Google Maps ({f.lat.toFixed(5)}, {f.lng.toFixed(5)})</a>}
      {!f.phone && !f.website && !f.hours_week && <p className="text-xs text-amber-600">Harta nuk ka telefon, web apo orar për këtë biznes. Shtoji me NDRYSHO.</p>}
    </div>
  );
}

function Editor({ f, set }) {
  const inp = (k, ph) => <Input data-testid={`biz-edit-${k}`} value={f[k] || ""} onChange={(e) => set({ [k]: e.target.value })} placeholder={ph} className="rounded-xl h-11" />;
  return (
    <div className="space-y-2" data-testid="biz-editor">
      {inp("title", "Emri i biznesit")}
      <select data-testid="biz-edit-country" value={f.country || ""} onChange={(e) => set({ country: e.target.value })} className={fld}>
        <option value="">Shteti</option>
        {[...new Set([f.country, ...COUNTRIES.map((c) => c.name)].filter(Boolean))].map((c) => <option key={c}>{c}</option>)}
      </select>
      {inp("city", "Qyteti")}{inp("address", "Adresa")}{inp("phone", "Telefoni p.sh. +46...")}{inp("website", "Website")}{inp("email", "Email")}
      {inp("logo", "Link i logos (opsional)")}
      <Input data-testid="biz-edit-hours_raw" value={f.hours_raw || ""} onChange={(e) => set({ hours_raw: e.target.value, hoursEdited: true, hours_week: null })} placeholder="Orari p.sh. Mo-Fr 10:00-22:00; Sa 12:00-22:00" className="rounded-xl h-11" />
      <Textarea data-testid="biz-edit-description" value={f.description || ""} onChange={(e) => set({ description: e.target.value })} placeholder="Përshkrimi" className="rounded-xl" />
    </div>
  );
}

export default function AdminBizImport() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState(null);
  const [edit, setEdit] = useState(false);
  const set = (p) => setF((x) => ({ ...x, ...p }));

  const search = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setBusy(true); setF(null); setItems(null);
    try { const { data } = await api.get("/admin/biz-search", { params: { q } }); setItems(data.items); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };
  const pick = async (r) => {
    setBusy(true); setEdit(false);
    const base = { osm: r.osm, title: r.name, address: r.address, city: r.city, country: r.country, lat: r.lat, lng: r.lng, category: "", subcategory: "" };
    try { const { data } = await api.get("/admin/biz-details", { params: { osm: r.osm } }); setF({ ...base, ...data }); }
    catch { setF(base); }
    finally { setBusy(false); }
  };
  const manual = () => { setF({ title: q, category: "", subcategory: "", country: "", city: "" }); setEdit(true); };
  const publish = async () => {
    if (!f.category) return toast.error("Zgjidh kategorinë");
    setBusy(true);
    try {
      let hours = f.hours_week;
      if (f.hours_raw && f.hoursEdited) { const { data } = await api.post("/admin/biz-hours", { text: f.hours_raw }); hours = data.hours_week || hours; }
      await api.post("/admin/biz-publish", { ...f, hours_week: hours });
      toast.success("U dërgua për aprovim te Pritje");
      setF(null); setItems(null); setQ("");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };
  const cat = cats().find((c) => c.key === f?.category);

  return (
    <div data-testid="admin-biz-import" className="space-y-3 bg-white rounded-2xl p-4 border border-slate-100">
      <form onSubmit={search} className="flex gap-2">
        <Input data-testid="biz-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Emri i biznesit" className="rounded-xl h-11 flex-1 min-w-0" />
        <Button data-testid="biz-search-btn" type="submit" disabled={busy} className="rounded-xl h-11 bg-emerald-500 hover:bg-emerald-600 font-bold">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-1" />KËRKO</>}</Button>
      </form>
      <p className="text-[11px] text-slate-400">Të dhënat merren nga harta e lirë OpenStreetMap. Çka mungon, e shton te NDRYSHO.</p>

      {items && !items.length && !f && (
        <div data-testid="biz-not-found" className="text-center py-4">
          <p className="text-sm text-slate-600 font-semibold">Biznesi nuk u gjet në hartë.</p>
          <Button data-testid="biz-manual-btn" variant="outline" onClick={manual} className="rounded-xl mt-2">Shto manualisht</Button>
        </div>
      )}
      {items && items.length > 0 && !f && <Results items={items} onPick={pick} />}

      {f && (
        <div className="space-y-3 border-t border-slate-100 pt-3">
          {edit ? <Editor f={f} set={set} /> : <Preview f={f} />}
          <div className="grid grid-cols-2 gap-2">
            <select data-testid="biz-category" value={f.category} onChange={(e) => set({ category: e.target.value, subcategory: "" })} className={fld}>
              <option value="">Kategoria *</option>
              {cats().map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
            <select data-testid="biz-subcategory" value={f.subcategory} onChange={(e) => set({ subcategory: e.target.value })} disabled={!cat} className={`${fld} disabled:text-slate-400`}>
              <option value="">Nënkategoria</option>
              {(cat?.subs || []).map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button data-testid="biz-edit-btn" variant="outline" onClick={() => setEdit((x) => !x)} className="rounded-xl h-11 font-bold">{edit ? "SHIKO" : "NDRYSHO"}</Button>
            <Button data-testid="biz-publish-btn" onClick={publish} disabled={busy} className="rounded-xl h-11 bg-emerald-500 hover:bg-emerald-600 font-bold">PUBLIKO</Button>
          </div>
          {items?.length > 0 && <button data-testid="biz-back-results" onClick={() => setF(null)} className="text-xs text-slate-500 underline">Kthehu te rezultatet</button>}
        </div>
      )}
    </div>
  );
}
